import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { useCart } from '../../contexts/CartContext'
import { useCurrency, price } from './templates/shared'
import { t } from '../../i18n'

// Full cart page (restaurants; ecommerce mostly uses the drawer). Styled with
// the storefront skin classes so it matches the template the owner picked.
export default function CartPage() {
  const currency = useCurrency()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { items, total, updateQuantity, removeItem, clearCart } = useCart()
  const query = searchParams.toString() ? `?${searchParams}` : ''
  const count = items.reduce((sum, item) => sum + item.quantity, 0)

  const changeQuantity = (item, quantity) => {
    if (quantity <= 0) { removeItem(item.id); toast.success(t("Item removed")) }
    else updateQuantity(item.id, quantity)
  }
  const clear = () => { if (window.confirm(t("Remove everything from your cart?"))) { clearCart(); toast.success(t("Cart cleared")) } }

  if (items.length === 0) {
    return (
      <section className="sk-page sk-card max-w-xl text-center py-14">
        <ShoppingBag className="mx-auto h-12 w-12 sk-muted" aria-hidden="true" />
        <h1 className="sk-heading mt-4">{t("Your cart is empty")}</h1>
        <p className="sk-muted mt-3">{t("Have a look around and add something you like.")}</p>
        <Link to={`/${query}`} className="sk-btn mt-8">{t("Continue shopping")} <ArrowRight size={16} /></Link>
      </section>
    )
  }

  return (
    <div className="sk-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">{count} {count === 1 ? t("item") : t("items")}</p>
          <h1 className="sk-heading">{t("Your cart")}</h1>
        </div>
        <button onClick={clear} className="sk-btn-ghost text-sm"><Trash2 size={15} /> {t("Clear cart")}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start">
        <ul className="sk-card p-0 divide-y divide-gray-100 min-w-0" aria-label={t("Items in your cart")}>
          {items.map(item => (
            <li key={item.id} className="flex gap-4 p-5 sk-divider">
              {item.image ? <img src={item.image} alt="" className="sk-thumb" /> : <div className="sk-thumb grid place-items-center"><ShoppingBag className="sk-muted" size={22} /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{item.name}</p>
                    {item.variantName && <p className="text-sm sk-muted">{item.variantName}</p>}
                    <p className="text-sm sk-muted">{price(item.unitPrice, currency)} {t("each")}</p>
                  </div>
                  <button onClick={() => { removeItem(item.id); toast.success(t("Item removed")) }} className="sk-muted hover:text-red-500 h-8" aria-label={t("Remove {name}", { name: item.name })}><Trash2 size={17} /></button>
                </div>
                {item.modifiers?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {item.modifiers.map(m => <span key={m.id} className="sk-chip">{m.name}{Number(m.extra_price) > 0 && ` +${Number(m.extra_price).toFixed(2)}`}</span>)}
                  </div>
                )}
                {item.note && <p className="text-sm sk-muted italic mt-2">&ldquo;{item.note}&rdquo;</p>}
                <div className="flex items-center justify-between gap-3 mt-3">
                  <div className="sk-qty">
                    <button onClick={() => changeQuantity(item, item.quantity - 1)} aria-label={t("Decrease quantity")}><Minus size={15} /></button>
                    <span aria-live="polite">{item.quantity}</span>
                    <button onClick={() => changeQuantity(item, item.quantity + 1)} aria-label={t("Increase quantity")}><Plus size={15} /></button>
                  </div>
                  <strong className="whitespace-nowrap">{price(item.total, currency)}</strong>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <aside className="sk-card lg:sticky lg:top-28">
          <h2 className="text-lg font-semibold mb-5">{t("Summary")}</h2>
          <div className="flex justify-between text-sm sk-muted mb-2"><span>{t("Items")}</span><span>{count}</span></div>
          <div className="flex justify-between text-lg font-semibold border-t sk-divider pt-4 mt-4"><span>{t("Subtotal")}</span><span>{price(total, currency)}</span></div>
          <p className="text-xs sk-muted mt-2">{t("Taxes and any service charge are added at checkout.")}</p>
          <button onClick={() => navigate(`/checkout${query}`)} className="sk-btn w-full mt-6">{t("Checkout")} <ArrowRight size={16} /></button>
          <Link to={`/${query}`} className="block text-center text-sm underline mt-4">{t("Continue shopping")}</Link>
        </aside>
      </div>
    </div>
  )
}
